/* eslint-disable import/no-duplicates */
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { Chart } from 'react-google-charts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useCurrentEffect } from '../../../../../../lib/use-current-effect';

import { usePatient } from '../../../../../../hooks/patient';
import { getMeasurementsByDay } from '../../../../../../services/metrics';

import MeasurementsArea from '../../MeasurementsArea';

import { ChildrenProps, ChildrenRefProps, DataChildrenProps } from '../..';
import { MetricTypeEnum } from '../../../../../../enums/MetricTypeEnum';
import formatNumber, { roundTo } from '../../../../../../utils/formatNumber';

const BodyTemperatureChart: React.ForwardRefRenderFunction<
  ChildrenRefProps,
  ChildrenProps
> = ({ dateFilter }, ref) => {
  const { patient } = usePatient();

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<DataChildrenProps>();
  const [cGraphData, setCGraphData] = useState<(string | number)[][]>([
    ['00:00', 0],
  ]);

  const handleChange = useCallback(
    async (patientId?: string, isCurrent = () => true) => {
      setIsLoading(true);
      if (!patientId) {
        return;
      }
      const response = await getMeasurementsByDay({
        patientId,
        metricType: MetricTypeEnum.BODY_TEMPERATURE,
        dateFilter,
      });
      if (response.length > 0) {
        const lastRecordTime = format(
          new Date(response[response.length - 1].mensuramentDate),
          "dd'/'MM'/'yyyy', ' HH'h'mm",
          { locale: ptBR },
        );

        const result = response.map(({ bodyTemperature, mensuramentDate }) => [
          format(new Date(mensuramentDate), 'HH:mm', {
            locale: ptBR,
          }),
          // Uma casa: o sensor manda float de 32 bits (`35.599998474121094`).
          roundTo(bodyTemperature, 1),
        ]);

        const value = formatNumber(Number(result[result.length - 1][1]), 1);

        if (isCurrent()) {
          setCGraphData(result);
          setData({
            lastRecordTime,
            value,
          });
        }
      } else if (isCurrent()) {
        setCGraphData([['00:00', 0, 0]]);
        setData(undefined);
      }

      setIsLoading(false);
    },
    [dateFilter],
  );

  useCurrentEffect(
    (isCurrent) => {
      (() => {
        if (patient?.id) {
          handleChange(patient.id, isCurrent);
        } else {
          setIsLoading(false);
        }
      })();
    },
    [dateFilter, handleChange, patient],
  );

  useImperativeHandle(ref, () => ({
    reload: () => handleChange(patient?.id),
  }));

  return (
    <MeasurementsArea
      data={{
        styleData: 'chart',
        isLoading,
        cardTitle: 'Temperatura (ºC)',
        dataDetails: data
          ? {
              title: 'último registro:',
              position: 'center',
              lastRecordTime: data.lastRecordTime,
              type: '°C',
              value: data.value,
              inAlert: false,
            }
          : undefined,
      }}
    >
      <Chart
        width="107%"
        height="95%"
        chartType="LineChart"
        style={{
          marginLeft: '-10px',
          marginTop: '5px',
        }}
        options={{
          legend: 'none',
          colors: ['#E12F54'],
          series: {
            0: { curveType: 'function' },
          },
          vAxis: {
            ticks: [34, 37, 40],
          },
        }}
        data={[['h', '°C'], ...cGraphData]}
        rootProps={{ 'data-testid': '7' }}
      />
    </MeasurementsArea>
  );
};

export default forwardRef(BodyTemperatureChart);
